import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Lock, Trophy, ArrowRight, X } from 'lucide-react';
import { Brain, Zap, Lightbulb, Target, Calculator, Puzzle } from 'lucide-react';
import { allCategories } from '../mock';

const iconMap = { Brain, Zap, Lightbulb, Target, Calculator, Puzzle };

// results: array of { category, correct }
const BrainSnapshot = ({ open, onClose, results, onSignup, onLogin }) => {
  const attempted = new Set(results.map((r) => r.category));
  const correctCount = results.filter((r) => r.correct).length;
  const total = results.length;
  const score = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  const scoreFor = (cat) => {
    const r = results.find((x) => x.category === cat);
    if (!r) return null;
    return r.correct ? 92 : 58;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="w-[calc(100vw-24px)] max-w-2xl bg-[#2A1810] border border-[#F4C430]/30 text-[#F5EDE0] p-0 rounded-3xl [&>button:not([data-testid])]:hidden max-h-[92vh] flex flex-col overflow-hidden"
      >
        <button
          onClick={onClose}
          className="!absolute top-3 right-3 sm:top-4 sm:right-4 h-9 w-9 rounded-full grid place-items-center border border-[#5a3d24] bg-[#2A1810]/80 backdrop-blur text-[#D9C9B3] hover:text-[#F4C430] hover:border-[#F4C430] z-20"
          aria-label="close"
          data-testid="snapshot-close-btn"
          style={{ display: 'grid' }}
        >
          <X size={16} />
        </button>

        <div className="relative p-5 sm:p-9 overflow-y-auto flex-1">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#F4C430]/15 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#5a3d24] px-3 py-1 text-xs text-[#F4C430]">
              <Trophy size={13} /> Your Brain Snapshot
            </div>

            <h3 className="mt-3 sm:mt-4 font-serif text-xl sm:text-4xl leading-tight">
              Nice work! Here's how your brain thinks today
            </h3>
            <p className="mt-1.5 sm:mt-2 text-[#B8A990] text-xs sm:text-sm">
              You attempted {total} of 6 categories — unlock your full report to see the rest
            </p>

            {/* Overall score */}
            <div className="mt-4 sm:mt-6 flex items-center gap-3 sm:gap-5 rounded-2xl bg-[#3D2817] border border-[#4a3221] p-3 sm:p-5">
              <ScoreRing pct={score} />
              <div className="min-w-0">
                <div className="text-[#B8A990] text-[10px] sm:text-xs uppercase tracking-widest">Snapshot score</div>
                <div className="font-serif text-2xl sm:text-3xl text-[#F5EDE0] leading-tight">{score}<span className="text-base sm:text-lg text-[#B8A990]">/100</span></div>
                <div className="text-xs sm:text-sm text-[#D9C9B3] mt-0.5">
                  {correctCount} correct out of {total} — you're off to a strong start!
                </div>
              </div>
            </div>

            {/* Category grid */}
            <div className="mt-4 sm:mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
              {allCategories.map((c) => {
                const Ico = iconMap[c.icon];
                const isAttempted = attempted.has(c.key);
                const cScore = scoreFor(c.key);
                return (
                  <div
                    key={c.key}
                    className={`relative rounded-xl border p-2.5 sm:p-3 ${
                      isAttempted
                        ? 'bg-[#3D2817] border-[#F4C430]/40'
                        : 'bg-[#3D2817]/60 border-[#4a3221]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg grid place-items-center bg-[#2A1810] border border-[#5a3d24] shrink-0">
                        {isAttempted ? (
                          <Ico size={12} className="text-[#F4C430]" />
                        ) : (
                          <Lock size={11} className="text-[#8f7658]" />
                        )}
                      </div>
                      <div className="text-xs sm:text-sm text-[#F5EDE0] font-medium truncate">{c.key}</div>
                    </div>
                    <div className="mt-1.5 sm:mt-2">
                      {isAttempted ? (
                        <>
                          <div className="text-[#F4C430] font-serif text-base sm:text-lg">{cScore}<span className="text-[10px] sm:text-xs text-[#B8A990]">/100</span></div>
                          <div className="h-1 sm:h-1.5 bg-[#2A1810] rounded-full overflow-hidden mt-1">
                            <div className="h-full bg-[#F4C430]" style={{ width: `${cScore}%` }} />
                          </div>
                        </>
                      ) : (
                        <div className="text-[10px] sm:text-[11px] text-[#8f7658]">Locked — sign up to reveal</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* STICKY FOOTER — always visible on mobile, no scrolling needed to reach CTAs */}
        <div
          className="shrink-0 border-t border-[#4a3221] bg-[#2A1810]/95 backdrop-blur px-4 py-3 sm:px-6 sm:py-4"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <div className="hidden sm:block mb-2">
            <div className="text-[#F5EDE0] font-serif text-base">Unlock your full report</div>
            <div className="text-xs text-[#B8A990]">Detailed scores, personalised plan and daily challenges</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <button
              onClick={onLogin}
              data-testid="snapshot-login-btn"
              className="w-full sm:w-auto px-5 h-11 rounded-full text-sm border border-[#5a3d24] text-[#F5EDE0] hover:border-[#F4C430] hover:text-[#F4C430] active:scale-[0.98]"
            >
              Log in
            </button>
            <Button
              onClick={onSignup}
              data-testid="snapshot-signup-btn"
              className="w-full sm:w-auto sm:ml-auto rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-11 px-5"
            >
              Unlock Full Report <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ScoreRing = ({ pct }) => {
  const size = 72;
  const r = 30;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div className="relative grid place-items-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#4a3221" strokeWidth="5" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#F4C430" strokeWidth="5" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        />
      </svg>
      <span className="absolute font-serif text-lg text-[#F5EDE0]">{pct}%</span>
    </div>
  );
};

export default BrainSnapshot;
